import { useState } from 'react';
import { Link } from 'react-router-dom';
import { api, ApiError, formatDate, newIdempotencyKey, scr } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { M11_ROLES, hasAnyRole } from '../../lib/roles';
import type { Page, TenderMethod, TenderNotice, TenderStatus } from '../../lib/types';
import { useApiQuery } from '../../lib/useApi';
import { EmptyState, ErrorNote, Field, Modal, PageHeader, Spinner, StatusBadge } from '../../ui/components';

const STATUSES: TenderStatus[] = [
  'DRAFT',
  'PUBLISHED',
  'CLARIFICATIONS',
  'CLOSED',
  'UNSEALED',
  'EVALUATION',
  'AWARD_PROPOSED',
  'STANDSTILL',
  'AWARDED',
  'CONTRACTED',
  'CANCELLED',
];
const METHODS: TenderMethod[] = ['OPEN', 'RESTRICTED', 'RFQ'];

/**
 * PUBLIC tender-browse list — no sign-in required (see src/lib/auth.tsx's
 * silent visitor-token bootstrap). DRAFT tenders are hidden server-side from
 * anyone without a staff role (assertVisible() in
 * packages/modules/m11-egp/src/routes/tenders.ts), so this same query
 * naturally shows fewer rows to a visitor/vendor than to procurement staff —
 * nothing filtered client-side.
 */
export function TenderList() {
  const { persona } = useAuth();
  const isOfficer = hasAnyRole(persona?.roles, M11_ROLES.PROCUREMENT_OFFICER);
  const [status, setStatus] = useState('');
  const [method, setMethod] = useState('');
  const [category, setCategory] = useState('');
  const [q, setQ] = useState('');
  const [showCreate, setShowCreate] = useState(false);

  const query = new URLSearchParams({ limit: '50' });
  if (status) query.set('status', status);
  if (method) query.set('method', method);
  if (category) query.set('category', category);
  if (q) query.set('q', q);

  const tendersQ = useApiQuery<Page<TenderNotice>>(`/api/v1/m11/tenders?${query.toString()}`);

  return (
    <div>
      <PageHeader
        title="Tenders"
        subtitle="Public tender register — open, results, and history. No sign-in required to browse."
        actions={isOfficer ? <button className="btn-primary" onClick={() => setShowCreate(true)}>+ Draft tender</button> : undefined}
      />

      <div className="mb-4 grid gap-2 sm:grid-cols-4">
        <Field label="Status" className="!mb-0">
          <select className="field-input" value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="">All statuses</option>
            {STATUSES.map((s) => (
              <option key={s} value={s}>{s.replaceAll('_', ' ')}</option>
            ))}
          </select>
        </Field>
        <Field label="Method" className="!mb-0">
          <select className="field-input" value={method} onChange={(e) => setMethod(e.target.value)}>
            <option value="">All methods</option>
            {METHODS.map((m) => (
              <option key={m} value={m}>{m}</option>
            ))}
          </select>
        </Field>
        <Field label="Category" className="!mb-0">
          <input className="field-input" value={category} onChange={(e) => setCategory(e.target.value)} placeholder="e.g. CONSTRUCTION" />
        </Field>
        <Field label="Search" className="!mb-0">
          <input className="field-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Title, ref no, MDA…" />
        </Field>
      </div>

      {tendersQ.error ? <ErrorNote message={tendersQ.error} /> : null}
      <div className="card overflow-x-auto">
        {tendersQ.loading ? (
          <Spinner />
        ) : (tendersQ.data?.items.length ?? 0) === 0 ? (
          <EmptyState title="No tenders match" hint="Try clearing a filter." />
        ) : (
          <table className="w-full">
            <thead>
              <tr>
                <th className="table-head">Ref</th>
                <th className="table-head">Title</th>
                <th className="table-head">MDA</th>
                <th className="table-head">Method</th>
                <th className="table-head">Status</th>
                <th className="table-head text-right">Est. value</th>
                <th className="table-head">Deadline</th>
              </tr>
            </thead>
            <tbody>
              {tendersQ.data!.items.map((t) => (
                <tr key={t.id} className="hover:bg-slate-50">
                  <td className="table-cell font-mono text-xs font-medium">
                    <Link className="text-brand-600 hover:underline" to={`/procurement/tenders/${t.id}`}>{t.refNo}</Link>
                  </td>
                  <td className="table-cell">{t.title}</td>
                  <td className="table-cell text-xs text-slate-500">{t.mdaCode}</td>
                  <td className="table-cell text-xs">{t.method}</td>
                  <td className="table-cell"><StatusBadge status={t.status} /></td>
                  <td className="table-cell text-right tabular-nums">{scr(t.estimatedValueScr)}</td>
                  <td className="table-cell text-xs">{formatDate(t.biddingDeadline)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {showCreate ? (
        <CreateTenderModal onClose={() => setShowCreate(false)} onDone={() => { setShowCreate(false); tendersQ.reload(); }} />
      ) : null}
    </div>
  );
}

function CreateTenderModal({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const [mdaCode, setMdaCode] = useState('');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('');
  const [method, setMethod] = useState<TenderMethod>('OPEN');
  const [estimatedValueScr, setEstimatedValueScr] = useState('');
  const [docFeeScr, setDocFeeScr] = useState('');
  const [bidBondScr, setBidBondScr] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.post(
        '/api/v1/m11/tenders',
        {
          mdaCode: mdaCode.toUpperCase(),
          title,
          description,
          category,
          method,
          estimatedValueScr: estimatedValueScr ? Number.parseFloat(estimatedValueScr) : undefined,
          docFeeScr: docFeeScr ? Number.parseFloat(docFeeScr) : undefined,
          bidBondScr: bidBondScr ? Number.parseFloat(bidBondScr) : undefined,
        },
        { idempotencyKey: newIdempotencyKey() },
      );
      onDone();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Failed to draft tender');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="Draft a new tender notice" onClose={onClose} wide>
      <div className="space-y-3">
        {error ? <ErrorNote message={error} /> : null}
        <div className="grid grid-cols-2 gap-3">
          <Field label="Owning MDA code">
            <input className="field-input" value={mdaCode} onChange={(e) => setMdaCode(e.target.value.toUpperCase())} placeholder="e.g. MOH, MOT, PUC" />
          </Field>
          <Field label="Method">
            <select className="field-input" value={method} onChange={(e) => setMethod(e.target.value as TenderMethod)}>
              {METHODS.map((m) => (
                <option key={m} value={m}>{m}</option>
              ))}
            </select>
          </Field>
        </div>
        <Field label="Title">
          <input className="field-input" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Supply of office furniture — Victoria HQ" />
        </Field>
        <Field label="Description">
          <textarea className="field-input" rows={3} value={description} onChange={(e) => setDescription(e.target.value)} />
        </Field>
        <Field label="Category" hint="Matches supplier categories / subscription alerts, e.g. CONSTRUCTION, IT, MEDICAL_SUPPLIES">
          <input className="field-input" value={category} onChange={(e) => setCategory(e.target.value)} />
        </Field>
        <div className="grid grid-cols-3 gap-3">
          <Field label="Estimated value (SCR)">
            <input className="field-input" type="number" min="0" value={estimatedValueScr} onChange={(e) => setEstimatedValueScr(e.target.value)} />
          </Field>
          <Field label="Document fee (SCR)">
            <input className="field-input" type="number" min="0" value={docFeeScr} onChange={(e) => setDocFeeScr(e.target.value)} />
          </Field>
          <Field label="Bid bond (SCR)">
            <input className="field-input" type="number" min="0" value={bidBondScr} onChange={(e) => setBidBondScr(e.target.value)} />
          </Field>
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <button className="btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn-primary" onClick={submit} disabled={busy || !mdaCode || title.length < 3 || description.length < 10 || category.length < 2}>
            {busy ? 'Drafting…' : 'Draft tender'}
          </button>
        </div>
        <p className="text-[11px] text-slate-400">
          After drafting: add at least one evaluation criterion and assign the evaluation panel (which mints the sealed-bid key) before you can publish.
        </p>
      </div>
    </Modal>
  );
}
